import React, { useCallback, useState } from 'react'

import {
  Button,
  Checkbox,
  Card,
  Notice,
  Icon,
  AttentionFilled,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func } from 'prop-types'

import I18n from 'Pods/Shared/I18n'
import JoinProgramModal from 'Pods/Shared/JoinProgramModal/JoinProgramModal'

const warningIcon = <Icon svgComponent={AttentionFilled} color="attention" />

function MaximizeRevenue({
  selectAllPrograms,
  sellForEquity,
  sellOnOtherPlatforms,
  isSaving = false,
  isSellForEquitySaved = false,
  showForceOptInWarning = false,
  forceOptInEnabled = false,
  handleSelectAllProgramsChange,
  handleSellForEquityChange,
  handleSellOnOtherPlatformsChange,
  handleJoinProgram,
}) {
  // Keep checkedColor stable across toggles to avoid Triangle UI's Checkbox
  // caching a stale iconColor (its effect deps exclude checkedColor).
  const checkedColor = showForceOptInWarning ? 'attention' : 'primary'
  const [isModalOpen, setIsModalOpen] = useState(false)
  const modalMode = sellForEquity ? 'info' : 'join'

  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', ...options }),
    []
  )

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handleConfirmModal = useCallback(() => {
    handleJoinProgram()
  }, [handleJoinProgram])

  const handleLearnMoreClick = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  // Intercept clicks in capture phase to prevent the Checkbox from toggling
  // its internal state before our onChange handler runs.
  const handleSellForEquityClickCapture = useCallback((e) => {
    if (sellForEquity && isSellForEquitySaved && !e.target.closest('.maximize-revenue__learn-more')) {
      e.stopPropagation()
      e.preventDefault()
      handleSellForEquityChange(false)
    }
  }, [sellForEquity, isSellForEquitySaved, handleSellForEquityChange])

  const handleSelectAllClickCapture = useCallback((e) => {
    if (selectAllPrograms && sellForEquity && isSellForEquitySaved) {
      e.stopPropagation()
      e.preventDefault()
      handleSelectAllProgramsChange(false)
    }
  }, [selectAllPrograms, sellForEquity, isSellForEquitySaved, handleSelectAllProgramsChange])

  return (
    <section className={classnames('maximize-revenue', showForceOptInWarning && 'maximize-revenue--force-opt-in')}>
      <h2 className="cgt-legacy-heading-h5">Maximize your earnings</h2>
      <p>
        Select these additional options if you want to increase your earnings
        with innovative selling methods. For these sales, CGTrader may adjust
        pricing and payout rates on your behalf:
      </p>

      {forceOptInEnabled && (
        <div className="maximize-revenue__banner">
          <Notice useSSRIcon ssrIcon={warningIcon} type="attention">
            <div className="cgt-ml-10">
              These earning programs have been enabled by default since May 15, 2026.
              See April 30 email for more details. Adjust the options below to fit your preferences.
            </div>
          </Notice>
        </div>
      )}

      <div className="sales-form__settings-container">
        <div onClickCapture={handleSelectAllClickCapture}>
          <Checkbox
            checkedColor="primary"
            id="checkbox-select-all-programs"
            label="Select all"
            isChecked={selectAllPrograms}
            onChange={handleSelectAllProgramsChange}
            isControlled
          />
        </div>
        <div className="sales-form__note-text">
          Note: Prices of models will be determined by CGTrader in each of the
          following methods.
        </div>

        <div className="sales-form__settings-panel">
          <Card hasBorder isRounded>
            <div onClickCapture={handleSellForEquityClickCapture}>
              <Checkbox
                key={`checkbox-sell-models-${checkedColor}`}
                checkedColor={checkedColor}
                id="checkbox-sell-models"
                label={
                  <span>
                    {'Sell my models for equity, custom enterprise deals, revenue share, '}
                    {'Subscription and other alternative compensation methods. '}
                    <Button
                      noWrap
                      size="small"
                      type="ghost-primary"
                      customClass="maximize-revenue__learn-more"
                      onClick={handleLearnMoreClick}
                    >
                      Learn more
                    </Button>
                  </span>
                }
                isChecked={sellForEquity}
                onChange={handleSellForEquityChange}
                isControlled
              />
            </div>

            <Checkbox
              key={`checkbox-sell-via-platforms-${checkedColor}`}
              checkedColor={checkedColor}
              id="checkbox-sell-via-platforms"
              label="Sell my models on other platforms via CGTrader partnerships"
              isChecked={sellOnOtherPlatforms}
              onChange={handleSellOnOtherPlatformsChange}
              isControlled
            />
          </Card>
        </div>
      </div>

      <JoinProgramModal
        isOpen={isModalOpen}
        isJoining={isSaving}
        mode={modalMode}
        redesigned
        onClose={handleCloseModal}
        onConfirm={handleConfirmModal}
        translate={translate}
      />
    </section>
  )
}

MaximizeRevenue.propTypes = {
  selectAllPrograms: bool.isRequired,
  sellForEquity: bool.isRequired,
  sellOnOtherPlatforms: bool.isRequired,
  isSaving: bool,
  isSellForEquitySaved: bool,
  showForceOptInWarning: bool,
  forceOptInEnabled: bool,
  handleSelectAllProgramsChange: func.isRequired,
  handleSellForEquityChange: func.isRequired,
  handleSellOnOtherPlatformsChange: func.isRequired,
  handleJoinProgram: func.isRequired,
}

export default MaximizeRevenue
